import {
    type AdditionalMetric,
    type AnyType,
    type BigNumberConfig,
    type CartesianChartConfig,
    type ChartConfig,
    type ChartType,
    type DataAppVizChartConfig,
    type CreateSavedChartVersion,
    type CustomDimension,
    type CustomFormat,
    type CustomVisConfig,
    type Dimension,
    type FieldId,
    type FunnelChartConfig,
    type GaugeChartConfig,
    type Item,
    type ItemsMap,
    type MapChartConfig,
    type Metric,
    type MetricQuery,
    type MetricType,
    type ParameterDefinitions,
    type PieChartConfig,
    type PreAggregateCheckResult,
    type ReplaceCustomFields,
    type SankeyChartConfig,
    type SavedChart,
    type TableCalculation,
    type TableCalculationMetadata,
    type TableChartConfig,
    type TimeZone,
    type TreemapChartConfig,
} from '@lightdash/common';
import { type QueryResultsProps } from '../../hooks/useQueryResults';

export enum ExplorerSection {
    FILTERS = 'FILTERS',
    VISUALIZATION = 'VISUALIZATION',
    CUSTOMVISUALIZATION = 'CUSTOMVISUALIZATION',
    RESULTS = 'RESULTS',
    SQL = 'SQL',
    PARAMETERS = 'PARAMETERS',
}

export enum ActionType {
    RESET,
    SET_TABLE_NAME,
    SET_FILTERS,
    SET_ROW_LIMIT,
    SET_TIME_ZONE,
    SET_COLUMN_ORDER,
    ADD_TABLE_CALCULATION,
    UPDATE_TABLE_CALCULATION,
    DELETE_TABLE_CALCULATION,
    SET_PREVIOUSLY_FETCHED_STATE,
    ADD_ADDITIONAL_METRIC,
    EDIT_ADDITIONAL_METRIC,
    REMOVE_ADDITIONAL_METRIC,
    TOGGLE_ADDITIONAL_METRIC_MODAL,
    TOGGLE_WRITE_BACK_MODAL,
    SET_PIVOT_FIELDS,
    SET_CHART_TYPE,
    SET_CHART_CONFIG,
    TOGGLE_EXPANDED_SECTION,
    ADD_CUSTOM_DIMENSION,
    EDIT_CUSTOM_DIMENSION,
    REMOVE_CUSTOM_DIMENSION,
    TOGGLE_CUSTOM_DIMENSION_MODAL,
    TOGGLE_FORMAT_MODAL,
    UPDATE_METRIC_FORMAT,
    UPDATE_DIMENSION_FORMAT,
    REPLACE_FIELDS,
    SET_PARAMETER_REFERENCES,
}

export type ChartConfigCache<T = AnyType> = {
    chartConfig: T;
    pivotConfig?: SavedChart['pivotConfig'];
};

/**
 * Map extent representing the current view state of a map.
 * Used for saving/restoring map position.
 */
export type MapExtent = {
    zoom: number;
    lat: number;
    lng: number;
};

// Extended cache type for map charts that includes temporary map extent
export type MapChartConfigCache = ChartConfigCache<MapChartConfig['config']> & {
    // Temporary map extent - updated on pan/zoom, read at save time
    // This is NOT used during render to avoid re-renders on map interaction
    tempMapExtent?: MapExtent | null;
};

export type ConfigCacheMap = {
    [ChartType.PIE]: ChartConfigCache<PieChartConfig['config']>;
    [ChartType.FUNNEL]: ChartConfigCache<FunnelChartConfig['config']>;
    [ChartType.BIG_NUMBER]: ChartConfigCache<BigNumberConfig['config']>;
    [ChartType.TABLE]: ChartConfigCache<TableChartConfig['config']>;
    [ChartType.CARTESIAN]: ChartConfigCache<CartesianChartConfig['config']>;
    [ChartType.TREEMAP]: ChartConfigCache<TreemapChartConfig['config']>;
    [ChartType.GAUGE]: ChartConfigCache<GaugeChartConfig['config']>;
    [ChartType.MAP]: MapChartConfigCache;
    [ChartType.CUSTOM]: ChartConfigCache<CustomVisConfig['config']>;
    [ChartType.SANKEY]: ChartConfigCache<SankeyChartConfig['config']>;
    [ChartType.DATA_APP_VIZ]: ChartConfigCache<DataAppVizChartConfig['config']>;
};

export type Action =
    | { type: ActionType.RESET; payload: ExplorerReduceState }
    | {
          type: ActionType.SET_PREVIOUSLY_FETCHED_STATE;
          payload: MetricQuery;
      }
    | { type: ActionType.SET_TABLE_NAME; payload: string }
    | { type: ActionType.SET_FILTERS; payload: MetricQuery['filters'] }
    | { type: ActionType.TOGGLE_EXPANDED_SECTION; payload: ExplorerSection }
    | {
          type: ActionType.SET_ROW_LIMIT;
          payload: number;
      }
    | {
          type: ActionType.SET_TIME_ZONE;
          payload: TimeZone;
      }
    | {
          type: ActionType.ADD_TABLE_CALCULATION;
          payload: TableCalculation;
      }
    | {
          type: ActionType.UPDATE_TABLE_CALCULATION;
          payload: { oldName: string; tableCalculation: TableCalculation };
      }
    | {
          type: ActionType.DELETE_TABLE_CALCULATION;
          payload: string;
      }
    | {
          type: ActionType.SET_COLUMN_ORDER;
          payload: string[];
      }
    | {
          type: ActionType.ADD_ADDITIONAL_METRIC;
          payload: AdditionalMetric;
      }
    | {
          type: ActionType.EDIT_ADDITIONAL_METRIC;
          payload: {
              additionalMetric: AdditionalMetric;
              previousAdditionalMetricName: string;
          };
      }
    | {
          type: ActionType.REMOVE_ADDITIONAL_METRIC;
          payload: FieldId;
      }
    | {
          type: ActionType.TOGGLE_ADDITIONAL_METRIC_MODAL;
          payload?: Omit<
              ExplorerReduceState['modals']['additionalMetric'],
              'isOpen'
          >;
      }
    | {
          type: ActionType.TOGGLE_WRITE_BACK_MODAL;
          payload?: Omit<ExplorerReduceState['modals']['writeBack'], 'isOpen'>;
      }
    | {
          type: ActionType.SET_PIVOT_FIELDS;
          payload: FieldId[];
      }
    | {
          type: ActionType.SET_CHART_TYPE;
          payload: {
              chartType: ChartType;
              cachedConfigs: Partial<ConfigCacheMap>;
          };
      }
    | {
          type: ActionType.SET_CHART_CONFIG;
          payload: {
              chartConfig: ChartConfig;
              cachedConfigs: Partial<ConfigCacheMap>;
          };
      }
    | {
          type: ActionType.ADD_CUSTOM_DIMENSION;
          payload: CustomDimension;
      }
    | {
          type: ActionType.EDIT_CUSTOM_DIMENSION;
          payload: {
              customDimension: CustomDimension;
              previousCustomDimensionId: string;
          };
      }
    | {
          type: ActionType.REMOVE_CUSTOM_DIMENSION;
          payload: FieldId;
      }
    | {
          type: ActionType.TOGGLE_CUSTOM_DIMENSION_MODAL;
          payload?: Omit<
              ExplorerReduceState['modals']['customDimension'],
              'isOpen'
          >;
      }
    | {
          type: ActionType.TOGGLE_FORMAT_MODAL;
          payload?: { item: Metric | Dimension };
      }
    | {
          type: ActionType.UPDATE_METRIC_FORMAT;
          payload: { metric: Metric; formatOptions: CustomFormat | undefined };
      }
    | {
          type: ActionType.UPDATE_DIMENSION_FORMAT;
          payload: {
              dimension: Dimension;
              formatOptions: CustomFormat | undefined;
          };
      }
    | {
          type: ActionType.REPLACE_FIELDS;
          payload: {
              fieldsToReplace: ReplaceCustomFields[string];
          };
      }
    | {
          type: ActionType.SET_PARAMETER_REFERENCES;
          payload: string[] | null;
      };

export type PreAggregateCheck =
    | {
          status: 'unavailable';
          reason: 'feature_disabled' | 'no_configured_pre_aggregates';
      }
    | {
          status: 'idle';
      }
    | {
          status: 'loading';
      }
    | {
          status: 'error';
          message: string;
      }
    | {
          status: 'ready';
          result: PreAggregateCheckResult;
      };

export type ChartSidebarStep = 'choose' | 'configure';

export type ChartTypeAuthoringState = {
    /** The chart type being revised; null while a new one is authored. */
    dataAppVizUuid: string | null;
    /** The older version the builder previews; null follows the current one. */
    viewedVersion: number | null;
    /** The type was created by this session, so cancelling may discard it. */
    createdInSession: boolean;
    /** What the chart and the sidebar showed before, restored on cancel. */
    previous: {
        chartSidebarStep: ChartSidebarStep;
        chartConfig: CreateSavedChartVersion['chartConfig'];
        pivotConfig: CreateSavedChartVersion['pivotConfig'];
    };
};

export interface ExplorerReduceState {
    expandedSections: ExplorerSection[];
    metadata?: {
        // Temporary state that tracks changes to `table calculations` - keeps track of new name and previous name to ensure these get updated correctly when making changes to the layout & config of a chart
        tableCalculations?: TableCalculationMetadata[];
    };
    isVisualizationConfigOpen?: boolean;
    /** Which step the chart gallery sidebar shows when it is open. */
    chartSidebarStep: ChartSidebarStep;
    /** A chart type being authored in place of the chart; null otherwise. */
    chartTypeAuthoring: ChartTypeAuthoringState | null;
    isMinimal?: boolean;
    isEditMode?: boolean;
    unsavedChartVersion: CreateSavedChartVersion;
    previouslyFetchedState?: MetricQuery;

    cachedChartConfigs: Partial<ConfigCacheMap>;
    /**
     * The parameters that are referenced in the query.
     * If null, this means we can't calculate the missing parameters, so we can't run the query.
     * If empty array, this means we know the missing parameters, but they are all optional, so we can run the query.
     */
    parameterReferences: string[] | null;
    parameterDefinitions: ParameterDefinitions;
    modals: {
        format: {
            isOpen: boolean;
            item?: Metric | Dimension;
        };
        additionalMetric: {
            isOpen: boolean;
            isEditing?: boolean;
            item?: Dimension | AdditionalMetric | CustomDimension | Metric;
            type?: MetricType;
        };
        customDimension: {
            isOpen: boolean;
            isEditing?: boolean;
            table?: string;
            item?: Dimension | CustomDimension;
        };
        writeBack: {
            isOpen: boolean;
            items?: CustomDimension[] | AdditionalMetric[];
        };
        itemDetail: {
            isOpen: boolean;
            itemType?: 'field' | 'table' | 'group';
            label?: string;
            description?: string;
            fieldItem?: Item | AdditionalMetric;
            tableMetadata?: {
                name: string;
                dbtPackageName?: string;
                ymlPath?: string;
                sqlPath?: string;
            };
        };
        periodOverPeriodComparison: {
            isOpen: boolean;
            metric?: Metric;
            itemsMap?: ItemsMap;
        };
    };

    // Query execution state - manages TanStack Query arguments and history
    queryExecution: {
        validQueryArgs: QueryResultsProps | null;
        unpivotedQueryArgs: QueryResultsProps | null;
        queryUuidHistory: string[];
        unpivotedQueryUuidHistory: string[];
        // Flag to trigger a query execution from components (works regardless of auto-fetch setting)
        pendingFetch: boolean;
    };
    fromDashboard?: string;
    isExploreFromHere?: boolean;
    savedChart?: SavedChart;
    unsavedColorPaletteUuid: string | null;

    // Pre-aggregate check state — computed once in useExplorerQueryEffects
    preAggregate: {
        usePreAggregateCache: boolean;
        check: PreAggregateCheck;
    };
}
